/**
 * Savings Page - Savings Plan for Young People
 * Tips and strategies for saving towards home purchase
 */

import { storage, requireAuth, requireProfile } from '../utils/storage.js';
import { renderNavbar } from '../components/navbar.js';
import { renderFooter } from '../components/footer.js';
import { formatCurrency, formatPercentage } from '../utils/calculations.js';

export function renderSavings(container) {
  if (!requireAuth()) return;
  if (!requireProfile()) return;

  const profile = storage.getProfile();
  const expenses = storage.getExpenses();

  // Target calculation
  const targetProperty = 200000;
  const totalNeeded = targetProperty * 0.32;
  const progress = Math.min(100, (profile.currentSavings / totalNeeded) * 100);

  container.innerHTML = `
    ${renderNavbar()}
    
    <main class="page page-background" style="padding-top: 100px;">
      <div class="container">
        
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 48px;">
          <span class="section-badge">Plan de Ahorro</span>
          <h1 style="margin-bottom: 12px;">Tu camino hacia la vivienda</h1>
          <p style="color: var(--gray-500); max-width: 600px; margin: 0 auto;">
            ${profile.age < 36
      ? 'Consejos diseñados específicamente para jóvenes como tú que quieren comprar su primera vivienda.'
      : 'Estrategias para optimizar tu ahorro y llegar antes a tu objetivo.'
    }
          </p>
        </div>
        
        <!-- Progress Card -->
        <div class="card" style="max-width: 700px; margin: 0 auto 48px; background: linear-gradient(135deg, var(--primary-500), var(--primary-700)); color: white;">
          <div style="display: flex; align-items: center; gap: 24px; flex-wrap: wrap;">
            <div style="flex: 1; min-width: 200px;">
              <h3 style="color: white; font-size: 18px; margin-bottom: 8px;">Tu progreso</h3>
              <div style="font-size: 36px; font-weight: 800;">${formatCurrency(profile.currentSavings)}</div>
              <div style="opacity: 0.8; font-size: 14px;">de ${formatCurrency(totalNeeded)} para vivienda de ${formatCurrency(targetProperty)}</div>
            </div>
            <div style="flex: 1; min-width: 200px;">
              <div style="margin-bottom: 8px; display: flex; justify-content: space-between; font-size: 14px;">
                <span>Progreso</span>
                <span>${formatPercentage(progress, 0)}</span>
              </div>
              <div style="height: 12px; background: rgba(255,255,255,0.3); border-radius: 6px; overflow: hidden;">
                <div style="width: ${progress}%; height: 100%; background: white; border-radius: 6px; transition: width 0.5s ease;"></div>
              </div>
              <div style="margin-top: 8px; font-size: 12px; opacity: 0.8;">
                Te faltan ${formatCurrency(Math.max(0, totalNeeded - profile.currentSavings))}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Tips Grid -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)); gap: 24px; margin-bottom: 48px;">
          
          <!-- Reduce Expenses -->
          <div class="card animate-slideUp">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px;">
              <div style="width: 48px; height: 48px; background: rgba(239, 68, 68, 0.1); border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center;">
                <i data-feather="scissors" style="color: var(--error);"></i>
              </div>
              <h3 style="font-size: 18px;">Reduce gastos</h3>
            </div>
            
            <div style="display: flex; flex-direction: column; gap: 16px;">
              <div style="padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                <div style="font-weight: 600; margin-bottom: 4px;">🏠 Vivienda compartida</div>
                <p style="font-size: 14px; color: var(--gray-600); margin: 0;">
                  Compartir piso puede ahorrarte 300-500€/mes frente a vivir solo.
                </p>
              </div>
              
              <div style="padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                <div style="font-weight: 600; margin-bottom: 4px;">📱 Audita tus suscripciones</div>
                <p style="font-size: 14px; color: var(--gray-600); margin: 0;">
                  Netflix, Spotify, gimnasio... ¿Las usas todas? Cancela las que no uses.
                </p>
              </div>
              
              <div style="padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                <div style="font-weight: 600; margin-bottom: 4px;">🍳 Cocina en casa</div>
                <p style="font-size: 14px; color: var(--gray-600); margin: 0;">
                  Comer fuera puede costarte 200-400€/mes extra. Meal prep = ahorro.
                </p>
              </div>
              
              <div style="padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                <div style="font-weight: 600; margin-bottom: 4px;">⚡ Compara suministros</div>
                <p style="font-size: 14px; color: var(--gray-600); margin: 0;">
                  Usa comparadores de luz, gas e internet. Puedes ahorrar 50-100€/mes.
                </p>
              </div>
            </div>
          </div>
          
          <!-- Increase Income -->
          <div class="card animate-slideUp" style="animation-delay: 0.1s;">
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px;">
              <div style="width: 48px; height: 48px; background: rgba(16, 185, 129, 0.1); border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center;">
                <i data-feather="trending-up" style="color: var(--success);"></i>
              </div>
              <h3 style="font-size: 18px;">Aumenta ingresos</h3>
            </div>
            
            <div style="display: flex; flex-direction: column; gap: 16px;">
              <div style="padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                <div style="font-weight: 600; margin-bottom: 4px;">💼 Pide una subida</div>
                <p style="font-size: 14px; color: var(--gray-600); margin: 0;">
                  Si llevas +1 año, prepara argumentos y negocia. El que no pide, no consigue.
                </p>
              </div>
              
              <div style="padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                <div style="font-weight: 600; margin-bottom: 4px;">🖥️ Freelance o side hustle</div>
                <p style="font-size: 14px; color: var(--gray-600); margin: 0;">
                  Usa tus habilidades para generar ingresos extra: diseño, programación, clases, etc.
                </p>
              </div>
              
              <div style="padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                <div style="font-weight: 600; margin-bottom: 4px;">📚 Invierte en formación</div>
                <p style="font-size: 14px; color: var(--gray-600); margin: 0;">
                  Mejora tus skills para acceder a puestos mejor pagados.
                </p>
              </div>
              
              <div style="padding: 16px; background: var(--gray-50); border-radius: var(--radius-lg);">
                <div style="font-weight: 600; margin-bottom: 4px;">🏷️ Vende lo que no uses</div>
                <p style="font-size: 14px; color: var(--gray-600); margin: 0;">
                  Wallapop, Vinted... Convierte objetos olvidados en dinero para tu entrada.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Savings Strategies -->
        <div class="card" style="margin-bottom: 48px;">
          <h3 style="margin-bottom: 24px; display: flex; align-items: center; gap: 8px;">
            <i data-feather="target" style="color: var(--primary-600);"></i>
            Estrategias de ahorro probadas
          </h3>
          
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px;">
            <div style="padding: 24px; background: linear-gradient(135deg, rgba(26, 82, 118, 0.08), rgba(26, 158, 111, 0.08)); border-radius: var(--radius-xl); border: 1px solid var(--primary-200);">
              <div style="font-size: 32px; margin-bottom: 12px;">📊</div>
              <h4 style="margin-bottom: 8px;">Regla 50/30/20</h4>
              <p style="font-size: 14px; color: var(--gray-600); margin: 0; line-height: 1.7;">
                <strong>50%</strong> para necesidades (alquiler, comida, transporte)<br>
                <strong>30%</strong> para deseos (ocio, caprichos)<br>
                <strong>20%</strong> para ahorro e inversión
              </p>
            </div>
            
            <div style="padding: 24px; background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1)); border-radius: var(--radius-xl); border: 1px solid var(--secondary-200);">
              <div style="font-size: 32px; margin-bottom: 12px;">🏦</div>
              <h4 style="margin-bottom: 8px;">Págate primero</h4>
              <p style="font-size: 14px; color: var(--gray-600); margin: 0; line-height: 1.7;">
                Nada más cobrar, transfiere automáticamente el 20% a una cuenta de ahorro separada. Lo que no ves, no lo gastas.
              </p>
            </div>
            
            <div style="padding: 24px; background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(217, 119, 6, 0.1)); border-radius: var(--radius-xl); border: 1px solid rgba(245, 158, 11, 0.3);">
              <div style="font-size: 32px; margin-bottom: 12px;">🎯</div>
              <h4 style="margin-bottom: 8px;">Reto de las 52 semanas</h4>
              <p style="font-size: 14px; color: var(--gray-600); margin: 0; line-height: 1.7;">
                Semana 1 ahorras 1€, semana 2 ahorras 2€... Al final del año habrás ahorrado <strong>1.378€</strong> extra.
              </p>
            </div>
          </div>
        </div>
        
        ${profile.age < 36 ? `
          <!-- Young People Specific -->
          <div class="card" style="border-left: 4px solid var(--primary-500);">
            <div style="display: flex; align-items: flex-start; gap: 16px;">
              <div style="font-size: 48px;">👨‍🎓</div>
              <div>
                <h3 style="margin-bottom: 8px;">Ventajas por ser menor de 36 años</h3>
                <p style="color: var(--gray-600); margin-bottom: 16px;">
                  Tienes acceso a ayudas especiales que pueden facilitarte mucho la compra:
                </p>
                <ul style="color: var(--gray-600); margin-left: 20px; line-height: 2;">
                  <li><strong>Aval ICO:</strong> El Estado avala hasta el 20% de tu hipoteca para primera vivienda</li>
                  <li><strong>Ayudas autonómicas:</strong> Subvenciones directas para jóvenes en ${profile.region}</li>
                  <li><strong>Deducciones fiscales:</strong> Algunos gastos son deducibles en la declaración de la renta</li>
                  <li><strong>Hipotecas jóvenes:</strong> Algunos bancos ofrecen condiciones especiales</li>
                </ul>
                <a href="/ayudas" data-link class="btn btn-primary" style="margin-top: 16px;">
                  <i data-feather="gift"></i>
                  Ver todas las ayudas disponibles
                </a>
              </div>
            </div>
          </div>
        ` : ''}
        
        <!-- CTA -->
        <div style="text-align: center; margin-top: 48px; padding: 40px; background: var(--gray-50); border-radius: var(--radius-2xl);">
          <h3 style="margin-bottom: 12px;">¿Quieres saber exactamente cuánto puedes ahorrar?</h3>
          <p style="color: var(--gray-500); margin-bottom: 24px;">
            Usa nuestra calculadora para analizar tus gastos y optimizar tu ahorro.
          </p>
          <a href="/gastos" data-link class="btn btn-primary btn-lg">
            <i data-feather="sliders"></i>
            Ir a la calculadora
          </a>
        </div>
        
      </div>
    </main>
    
    ${renderFooter()}

    <style>
      .page-background {
        background: 
          radial-gradient(circle at 20px 20px, var(--primary-100) 2px, transparent 0),
          linear-gradient(135deg, var(--primary-50) 0%, white 100%);
        background-size: 40px 40px, 100% 100%;
        background-attachment: fixed;
      }
    </style>
  `;
}
